import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Users, Car, Banknote, Activity, TrendingUp, TrendingDown, CheckCircle, Clock, Package } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from '../../hooks/use-toast';
import AdminLayout from '../../components/admin/AdminLayout';

const StatCard = ({ title, value, icon: Icon, trend, trendValue, color }) => (
  <Card className="p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-600 mb-1">{title}</p>
        <p className="text-3xl font-bold text-gray-900">{value}</p>
        {trend && (
          <div className={`flex items-center gap-1 mt-2 text-sm ${
            trend === 'up' ? 'text-green-600' : 'text-red-600'
          }`}>
            {trend === 'up' ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
            <span>{trendValue}</span>
          </div>
        )}
      </div>
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
    </div>
  </Card>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      
      // Fetch stats
      const statsResponse = await api.get('/admin/dashboard/stats');
      setStats(statsResponse.data.stats);

      // Fetch revenue analytics
      const revenueResponse = await api.get('/admin/analytics/revenue?days=30');
      setRevenueData(revenueResponse.data.analytics);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard data',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">Overview of your platform</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Users"
            value={stats?.totalUsers || 0}
            icon={Users}
            trend="up"
            trendValue={`+${stats?.newUsersToday || 0} today`}
            color="bg-gradient-to-br from-blue-500 to-blue-600"
          />
          <StatCard
            title="Total Rides"
            value={stats?.totalRides || 0}
            icon={Car}
            trend="up"
            trendValue={`+${stats?.todayRides || 0} today`}
            color="bg-gradient-to-br from-green-500 to-green-600"
          />
          <StatCard
            title="Packages Delivered"
            value={stats?.completedPackages || 0}
            icon={Package}
            trend="up"
            trendValue={`+${stats?.todayPackages || 0} today`}
            color="bg-gradient-to-br from-orange-500 to-orange-600"
          />
          <StatCard
            title="Ride Revenue"
            value={`K${stats?.totalRevenue || 0}`}
            icon={Banknote}
            color="bg-gradient-to-br from-purple-500 to-purple-600"
          />
        </div>

        {/* Revenue Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <StatCard
            title="Commission Revenue"
            value={`K${stats?.totalCommissionRevenue || 0}`}
            icon={Banknote}
            color="bg-gradient-to-br from-green-600 to-teal-600"
          />
          <StatCard
            title="Total Packages"
            value={stats?.totalPackages || 0}
            icon={Package}
            color="bg-gradient-to-br from-yellow-500 to-yellow-600"
          />
        </div>

        {/* Driver Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatCard
            title="Total Drivers"
            value={stats?.totalDrivers || 0}
            icon={Car}
            color="bg-gradient-to-br from-orange-500 to-orange-600"
          />
          <StatCard
            title="Approved Drivers"
            value={stats?.approvedDrivers || 0}
            icon={CheckCircle}
            color="bg-gradient-to-br from-green-500 to-green-600"
          />
          <StatCard
            title="Online Drivers"
            value={stats?.onlineDrivers || 0}
            icon={Activity}
            color="bg-gradient-to-br from-teal-500 to-teal-600"
          />
          <StatCard
            title="Pending Applications"
            value={stats?.pendingDriverApplications || 0}
            icon={Clock}
            trend={stats?.pendingDriverApplications > 0 ? "up" : undefined}
            trendValue={stats?.pendingDriverApplications > 0 ? "Needs review" : ""}
            color="bg-gradient-to-br from-yellow-500 to-yellow-600"
          />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Chart */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Revenue Trend (Last 30 Days)</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#e91e63" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          {/* Rides Chart */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Rides Volume (Last 30 Days)</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="rides" fill="#9c27b0" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6">
            <h3 className="text-sm font-medium text-gray-600 mb-2">This Week</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-700">New Users</span>
                <span className="font-semibold">{stats?.newUsersWeek || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Rides</span>
                <span className="font-semibold">{stats?.weekRides || 0}</span>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-sm font-medium text-gray-600 mb-2">This Month</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-700">New Users</span>
                <span className="font-semibold">{stats?.newUsersMonth || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Rides</span>
                <span className="font-semibold">{stats?.monthRides || 0}</span>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Today</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-700">New Users</span>
                <span className="font-semibold">{stats?.newUsersToday || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Rides</span>
                <span className="font-semibold">{stats?.todayRides || 0}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;